import React from 'react';
import { Loader2, Clock, Download, Pencil, Trash2, Plus, X, Settings as SettingsIcon } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { TimeClockSettings } from './TimeClockSettings';

interface TimeClockEntry {
    id: string;
    employe_id: string;
    first_name?: string;
    last_name?: string;
    job_post_title?: string;
    clock_in: string;
    clock_out: string | null;
    note: string;
}

interface EmployeeOption {
    id: string;
    first_name: string;
    last_name: string;
}

const employeeName = (r: { first_name?: string; last_name?: string }) => `${r.first_name || ''} ${r.last_name || ''}`.trim() || 'Salarié';

/** `null` = pas encore pointé (aucune fin) ; `false` = fin antérieure au début (donnée
 *  incohérente, à corriger) — distinct de 0, qu'il ne faut jamais afficher pour ce cas. */
const formatDuration = (startIso: string, endIso: string | null): string | null | false => {
    if (!endIso) return null;
    const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
    if (ms < 0) return false;
    const totalMinutes = Math.round(ms / 60000);
    return `${Math.floor(totalMinutes / 60)}h${String(totalMinutes % 60).padStart(2, '0')}`;
};

const toLocalInputValue = (iso: string | null) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInputValue = (v: string) => (v ? new Date(v).toISOString() : '');

interface EntryFormState {
    id?: string;
    employe_id: string;
    clock_in: string;
    clock_out: string;
    note: string;
}

const emptyForm: EntryFormState = { employe_id: '', clock_in: '', clock_out: '', note: '' };

export const PointageManagement: React.FC = () => {
    const [employees, setEmployees] = React.useState<EmployeeOption[]>([]);
    const [entries, setEntries] = React.useState<TimeClockEntry[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [filterEmployee, setFilterEmployee] = React.useState('');
    const [filterStart, setFilterStart] = React.useState('');
    const [filterEnd, setFilterEnd] = React.useState('');
    const [form, setForm] = React.useState<EntryFormState | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [showSettings, setShowSettings] = React.useState(false);

    const load = React.useCallback(() => {
        setLoading(true);
        moduleApi.getTimeClockEntries({ employe_id: filterEmployee || undefined, start: filterStart || undefined, end: filterEnd || undefined })
            .then((d: any) => setEntries(Array.isArray(d) ? d : []))
            .catch(() => setEntries([]))
            .finally(() => setLoading(false));
    }, [filterEmployee, filterStart, filterEnd]);
    React.useEffect(() => { load(); }, [load]);
    React.useEffect(() => {
        moduleApi.getEmployes().then((d: any) => setEmployees(Array.isArray(d) ? d : [])).catch(() => setEmployees([]));
    }, []);

    const handleExport = async () => {
        setExporting(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (filterEmployee) params.set('employe_id', filterEmployee);
            if (filterStart) params.set('start', filterStart);
            if (filterEnd) params.set('end', filterEnd);
            const res = await fetch(`/api/timeclock/export${params.toString() ? `?${params.toString()}` : ''}`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) return;
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = 'pointage.csv';
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } finally {
            setExporting(false);
        }
    };

    const openAdd = () => setForm({ ...emptyForm, employe_id: employees[0]?.id || '' });
    const openEdit = (e: TimeClockEntry) => setForm({ id: e.id, employe_id: e.employe_id, clock_in: toLocalInputValue(e.clock_in), clock_out: toLocalInputValue(e.clock_out), note: e.note || '' });

    const handleSave = async () => {
        if (!form) return;
        if (!form.employe_id || !form.clock_in) { setError('Salarié et heure de début requis.'); return; }
        if (form.clock_out && new Date(form.clock_out).getTime() <= new Date(form.clock_in).getTime()) {
            setError("L'heure de fin doit être après l'heure de début.");
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = { employe_id: form.employe_id, clock_in: fromLocalInputValue(form.clock_in), clock_out: form.clock_out ? fromLocalInputValue(form.clock_out) : undefined, note: form.note.trim() };
            const res: any = form.id
                ? await moduleApi.updateTimeClockEntry(form.id, { clock_in: payload.clock_in, clock_out: payload.clock_out, note: payload.note })
                : await moduleApi.addTimeClockEntry(payload as any);
            if (res?.error) { setError(res.error); return; }
            setForm(null);
            load();
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!confirm('Supprimer cette entrée de pointage ?')) return;
        await moduleApi.deleteTimeClockEntry(id);
        load();
    };

    return (
        <div className="space-y-6">
            <header className="flex items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-bold tracking-tight text-content flex items-center gap-2"><Clock size={18} /> Pointage</h1>
                    <p className="text-content-muted text-sm mt-0.5">Heures pointées par vos salariés depuis leur portail (début/fin de service).</p>
                </div>
                <button onClick={() => setShowSettings(true)}
                    className="p-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft transition-all shrink-0" title="Réglages du pointage">
                    <SettingsIcon size={18} />
                </button>
            </header>

            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1 block">Salarié</label>
                    <select value={filterEmployee} onChange={(e) => setFilterEmployee(e.target.value)} className="bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500">
                        <option value="">Tous</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{employeeName(e)}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1 block">Du</label>
                    <input type="date" value={filterStart} onChange={(e) => setFilterStart(e.target.value)} className="bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                </div>
                <div>
                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest mb-1 block">Au</label>
                    <input type="date" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)} className="bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                </div>
                <div className="flex-1" />
                <button onClick={handleExport} disabled={exporting} className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all disabled:opacity-50">
                    <Download size={14} /> {exporting ? 'Export...' : 'Exporter (CSV)'}
                </button>
                <button onClick={openAdd} className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold transition-all">
                    <Plus size={14} /> Ajouter manuellement
                </button>
            </div>

            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}

            {loading ? (
                <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>
            ) : entries.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border rounded-2xl">
                    <Clock size={28} className="mx-auto mb-2 text-content-muted opacity-40" />
                    <p className="text-content-muted text-sm">Aucun pointage pour cette période.</p>
                </div>
            ) : (
                <div className="bg-white dark:bg-surface border border-border rounded-2xl overflow-x-auto">
                    <table className="w-full text-left min-w-[700px]">
                        <thead className="bg-slate-100 dark:bg-black/60 border-b border-border">
                            <tr>
                                <th className="px-5 py-3 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Salarié</th>
                                <th className="px-5 py-3 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Poste</th>
                                <th className="px-5 py-3 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Début</th>
                                <th className="px-5 py-3 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Fin</th>
                                <th className="px-5 py-3 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted">Durée</th>
                                <th className="px-5 py-3 text-xs uppercase tracking-widest text-slate-700 dark:text-content-muted text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                            {entries.map((e) => (
                                <tr key={e.id} className="hover:bg-brand-600/10 transition-colors">
                                    <td className="px-5 py-3 text-slate-900 dark:text-content font-medium">{employeeName(e)}</td>
                                    <td className="px-5 py-3 text-slate-700 dark:text-content-muted">{e.job_post_title || '-'}</td>
                                    <td className="px-5 py-3 text-slate-700 dark:text-content-muted">{new Date(e.clock_in).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                                    <td className="px-5 py-3 text-slate-700 dark:text-content-muted">{e.clock_out ? new Date(e.clock_out).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : <span className="text-emerald-500 font-bold">en cours</span>}</td>
                                    <td className="px-5 py-3 tabular-nums">
                                        {(() => {
                                            const d = formatDuration(e.clock_in, e.clock_out);
                                            if (d === false) return <span className="text-red-500 font-bold" title="La fin est antérieure au début — corrigez ce pointage">Fin avant début</span>;
                                            if (d === null) return <span className="text-slate-700 dark:text-content-muted">—</span>;
                                            return <span className="text-slate-700 dark:text-content-muted">{d}</span>;
                                        })()}
                                    </td>
                                    <td className="px-5 py-3">
                                        <div className="flex justify-end gap-2">
                                            <button onClick={() => openEdit(e)} className="p-2 rounded-lg text-slate-700 dark:text-gray-200 hover:bg-slate-100 dark:hover:bg-slate-200 dark:bg-surface-soft transition-colors"><Pencil size={14} /></button>
                                            <button onClick={() => handleDelete(e.id)} className="p-2 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 size={14} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {form && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
                        <div className="flex items-center justify-between p-5 border-b border-border">
                            <h2 className="text-base font-bold text-content flex items-center gap-2"><Clock size={17} className="text-brand-500" /> {form.id ? 'Corriger le pointage' : 'Ajouter un pointage'}</h2>
                            <button onClick={() => setForm(null)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Salarié</label>
                                <select value={form.employe_id} onChange={(e) => setForm({ ...form, employe_id: e.target.value })} disabled={!!form.id}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 disabled:opacity-60">
                                    {employees.map((e) => <option key={e.id} value={e.id}>{employeeName(e)}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Début</label>
                                <input type="datetime-local" value={form.clock_in} onChange={(e) => setForm({ ...form, clock_in: e.target.value })}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Fin (optionnel)</label>
                                <input type="datetime-local" value={form.clock_out} onChange={(e) => setForm({ ...form, clock_out: e.target.value })}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-content-muted uppercase tracking-widest mb-1.5 block">Note (optionnel)</label>
                                <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                            </div>
                            {error && <p className="text-xs text-red-500 font-semibold">{error}</p>}
                        </div>
                        <div className="flex justify-end gap-3 p-5 border-t border-border">
                            <button onClick={() => setForm(null)} className="px-4 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft font-bold text-sm transition-all">Annuler</button>
                            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all disabled:opacity-50">
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showSettings && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-end p-3 border-b border-border shrink-0">
                            <button onClick={() => setShowSettings(false)} className="p-1.5 text-content-muted hover:text-content rounded-lg hover:bg-surface-soft"><X size={18} /></button>
                        </div>
                        <div className="p-5 pt-0 overflow-y-auto">
                            <TimeClockSettings employees={employees} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PointageManagement;
